/**
 * ReplayMomentCard Component
 *
 * Bottom card component that shows metadata about the current moment
 * in the writing replay. Teachers see at a glance what happened, when,
 * and how long it took.
 *
 * Supports two views:
 * 1. Key Moments View: Shows moment summary with drill-down affordance
 * 2. Drilled Down View: Shows individual edit details
 *
 * @example
 * ```tsx
 * // Key Moments View
 * <ReplayMomentCard
 *   moment={currentMoment}
 *   isDrilledDown={false}
 *   onDrillDown={() => setDrilledDown(true)}
 * />
 *
 * // Drilled Down View
 * <ReplayMomentCard
 *   moment={currentMoment}
 *   isDrilledDown={true}
 *   editIndexInMoment={2}
 * />
 * ```
 */

import PiClipboardCheckStroke from "@markeroo/icons/src/stroke/files-&-folders/PiClipboardCheckStroke";
import PiKeyboardWiredStroke from "@markeroo/icons/src/stroke/devices/PiKeyboardWiredStroke";
import PiDeleteDustbin01Stroke from "@markeroo/icons/src/stroke/general/PiDeleteDustbin01Stroke";
import PiRefreshStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiRefreshStroke";
import PiUturnLeftStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiUturnLeftStroke";
import PiChevronDownStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronDownStroke";

import { formatTimestamp } from "../core/formatters";
import type { KeyMoment, KeyMomentType } from "../types";

/**
 * Strings for the MomentCard component.
 * These can be overridden via props for i18n.
 */
export interface ReplayMomentCardStrings {
  momentTypePaste: string;
  momentTypeTyped: string;
  momentTypeDeleted: string;
  momentTypeSessionStart: string;
  momentTypeUndo: string;
  thisContainsEdits: (count: number) => string;
  seeEdits: string;
  editsLabel: string;
  highlightPosition: (current: number, total: number) => string;
}

const defaultStrings: ReplayMomentCardStrings = {
  momentTypePaste: "Paste event",
  momentTypeTyped: "Typing event",
  momentTypeDeleted: "Deletion event",
  momentTypeSessionStart: "Session start",
  momentTypeUndo: "Undo event",
  thisContainsEdits: (count) => `This contains ${count} edit${count === 1 ? "" : "s"}`,
  seeEdits: "See edits",
  editsLabel: "edits",
  highlightPosition: (current, total) => `${current} of ${total}`,
};

/**
 * Icon configuration for each moment type.
 * Maps moment types to their respective icons and color classes.
 *
 * Colors use semantic theme tokens:
 * - paste: warning (amber) - represents pasted content that needs attention
 * - typed: success-text (green) - represents organic student typing
 * - deleted: error (red) - represents removed content
 * - session_start: brand (blue) - neutral session marker
 * - undo: muted (gray) - represents undone actions
 */
function getMomentTypeConfig(strings: ReplayMomentCardStrings): Record<
  KeyMomentType,
  {
    Icon: React.ComponentType<{ size?: number; className?: string }>;
    colorClass: string;
    ariaLabel: string;
  }
> {
  return {
    paste: {
      Icon: PiClipboardCheckStroke,
      colorClass: "text-semantic-warning",
      ariaLabel: strings.momentTypePaste,
    },
    typed: {
      Icon: PiKeyboardWiredStroke,
      colorClass: "text-semantic-success-text",
      ariaLabel: strings.momentTypeTyped,
    },
    deleted: {
      Icon: PiDeleteDustbin01Stroke,
      colorClass: "text-semantic-error",
      ariaLabel: strings.momentTypeDeleted,
    },
    session_start: {
      Icon: PiRefreshStroke,
      colorClass: "text-brand-600",
      ariaLabel: strings.momentTypeSessionStart,
    },
    undo: {
      Icon: PiUturnLeftStroke,
      colorClass: "text-theme-muted",
      ariaLabel: strings.momentTypeUndo,
    },
  };
}

export interface ReplayMomentCardProps {
  /** The current moment being displayed */
  moment: KeyMoment;
  /** Whether we're in drilled-down view */
  isDrilledDown: boolean;
  /** Index of current edit within moment (when drilled down) */
  editIndexInMoment?: number;
  /** Called when user clicks drill-down button */
  onDrillDown?: () => void;
  /** Optional className */
  className?: string;
  /** Override strings for i18n */
  strings?: Partial<ReplayMomentCardStrings>;
}

export function ReplayMomentCard({
  moment,
  isDrilledDown,
  editIndexInMoment,
  onDrillDown,
  className = "",
  strings: stringOverrides,
}: ReplayMomentCardProps) {
  const strings = { ...defaultStrings, ...stringOverrides };
  const momentTypeConfig = getMomentTypeConfig(strings);
  const config = momentTypeConfig[moment.type];
  const { Icon, colorClass, ariaLabel } = config;

  // Determine if drill-down affordance should be shown
  // Only show when: not drilled down AND moment has more than 1 event
  const showDrillDown = !isDrilledDown && moment.events.length > 1;

  // Format timestamp for display
  const formattedTimestamp = formatTimestamp(moment.timestamp);

  return (
    <article
      className={`bg-surface-secondary border border-border rounded-lg p-4 ${className}`}
      aria-label={`${ariaLabel}: ${moment.summary}`}
    >
      {/* Header Row: Icon, Summary, Timestamp/Duration */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <span className={colorClass} aria-hidden="true">
            <Icon size={24} />
          </span>

          {/* Summary */}
          <h2 className="text-lg font-semibold text-text-theme-primary uppercase tracking-wide">
            {moment.summary}
          </h2>
        </div>

        {/* Timestamp and Duration */}
        <div className="text-sm text-text-theme-secondary whitespace-nowrap">
          {formattedTimestamp}
          <span className="mx-1" aria-hidden="true">
            {"\u00B7"}
          </span>
          <span>{moment.duration}</span>
        </div>
      </div>

      {/* Drill-down affordance (only when not drilled down and multiple events) */}
      {showDrillDown && (
        <div className="mt-3 border-t border-border pt-3">
          <div className="flex items-center justify-between bg-surface-primary rounded-md px-3 py-2">
            <span className="text-sm text-text-theme-secondary">
              {strings.thisContainsEdits(moment.events.length)}
            </span>
            <button
              type="button"
              onClick={onDrillDown}
              className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1"
              aria-label={`${strings.seeEdits} - ${moment.events.length} ${strings.editsLabel}`}
            >
              {strings.seeEdits}
              <PiChevronDownStroke size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Edit index indicator when drilled down */}
      {isDrilledDown && typeof editIndexInMoment === "number" && (
        <div className="mt-2 text-sm text-text-theme-secondary">
          {/* Screen reader announcement for current edit position */}
          <span className="sr-only">
            {strings.highlightPosition(editIndexInMoment + 1, moment.events.length)}
          </span>
          <span aria-hidden="true">
            {editIndexInMoment + 1}{" / "}{moment.events.length}
          </span>
        </div>
      )}
    </article>
  );
}

export default ReplayMomentCard;
